import AccordionSummary from "@mui/material/AccordionSummary";
import TextField from "@mui/material/TextField";
import {Autocomplete, CircularProgress} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import React, {useEffect, useState} from "react";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import ConfirmationDialog from "../Dialogs/ConfirmationDialog";
import NumberInput from "../Inputs/NumberInput";
import {produce} from "immer";
import {useTheme} from "@mui/material/styles";

function ProjectDetailRowAccordionSummary({
                                            quoteDetailId,
                                            handleProductRemoveFromQuote,
                                            productItems,
                                            productItemIds,
                                            productGroups,
                                            productGroupIds,
                                            isSubmitting,
                                            onChange,
                                            quoteTemplates,
                                            quoteTemplatesStatus, 
                                            productsStatus,
                                            setExpanded,
                                            setInitialTemplateQuoteDetail,
                                            allQuoteDetailsSaved,
                                            draftQuoteDetail,
                                            setDraftQuoteDetail, 
                                            formValidQuoteDetail
                                        }) 
{
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [quantityValue, setQuantityValue] = useState(1);
    
    const theme = useTheme();

    useEffect(() => {
        setLoading(!(productsStatus === 'succeeded' && quoteTemplatesStatus === 'succeeded') &&
            !(productsStatus === 'failed' || quoteTemplatesStatus === 'failed'));
    }, [productGroups, productItems, quoteTemplates]);
    
    const handleQuantityChange = (newValue) => {
        setQuantityValue(newValue);
        setDraftQuoteDetail(produce(draftQuoteDetail, draft => {
            draft.quantity = newValue;
        }))
    }
    const handleProductSelection = (event, newValue) => {
        const errorMessage = newValue === null ? "Please choose an option." : false;
        setValue(newValue);
        setError(errorMessage);
        onChange(!errorMessage);
        if (!errorMessage) {
            setExpanded(true);
            const selectedKey = newValue.key;
            if (productGroupIds.includes(selectedKey)) {
                //selected value is a Product Group
                setDraftQuoteDetail({
                    tempId: quoteDetailId,
                    productGroupId: selectedKey,
                    quantity: 1,
                    quoteDetailFieldValues: productGroups[selectedKey]?.fields?.map((field) => ({
                        fieldId: field.id,
                        value: field.type === 4 || field.type === "Boolean" ? "NO" : ""
                    })),
                    categoryItems: []
                });

                setInitialTemplateQuoteDetail();
                
                formValidQuoteDetail.current = { id: true };
                productGroups[selectedKey]?.fields?.map((field) => {
                    formValidQuoteDetail.current[field.id] = false;
                });
            } else if (productItemIds.includes(selectedKey)) {
                // selected value is a Product item
                setDraftQuoteDetail({
                    tempId: quoteDetailId,
                    productItemId: selectedKey,
                    quantity: 1,
                    quoteDetailFieldValues: [
                        {
                            productItemId: selectedKey,
                            fields: productItems[selectedKey]?.fields?.map((field) => ({
                                fieldId: field.id,
                                value: field.type === 4 || field.type === "Boolean" ? "NO" : ""
                            }))
                        }
                    ],
                });

                setInitialTemplateQuoteDetail();

                formValidQuoteDetail.current = { id: true };
                productItems[selectedKey]?.fields.map((field) => {
                    formValidQuoteDetail.current[field.id] = field.type === 4 || field.type === "Boolean";
                });
            } else {
                // Handle a template
                const template = quoteTemplates.find(template => template.id === selectedKey);
                if (template) {
                    const draftQuoteDetailToSet = {
                        tempId: quoteDetailId,
                        quantity: 1,
                        templateDetails: {
                            templateId: selectedKey,
                            templateName: template.name
                        },
                        templateId: null,
                        templateName: null,
                        ...template.template
                    };
                    setDraftQuoteDetail(draftQuoteDetailToSet);
                    setInitialTemplateQuoteDetail(draftQuoteDetailToSet);

                    formValidQuoteDetail.current = { id: true };
                    if (template.productItemId) {
                        productItems[template.productItemId]?.fields.map((field) => {
                            formValidQuoteDetail.current[field.id] = true;
                        });
                    } else if (template.productGroupId) {
                        draftQuoteDetailToSet.categoryItems.map((category) => {
                            formValidQuoteDetail.current[category.productCategoryId] = formValidQuoteDetail.current[category.productCategoryId] ?? { id: true };
                            formValidQuoteDetail.current[category.productCategoryId][category.productItemId] = {};
                            category.fields.map((field) => {
                                formValidQuoteDetail.current[category.productCategoryId][category.productItemId][field.fieldId] = !!field.value;
                            })
                        });
                    }
                }
            }
        } else {
            setExpanded(false);
            setDraftQuoteDetail( {
                tempId: quoteDetailId,
                quoteDetailFieldValues: [],
            });
            formValidQuoteDetail.current = { id: false };
        }
    };

    const productOptions = ['Templates', 'Product Groups', 'Product Items'].flatMap((productType) => {
        if (productType === 'Product Groups') {
            return productGroupIds.map(groupId => ({
                key: groupId,
                title: productGroups[groupId].name,
                productType,
                description: productGroups[groupId].description,
                code: productGroups[groupId].businessCode
            }));
        } else if (productType === 'Templates') {
            return quoteTemplates.map(template => ({
                key: template.id,
                title: template.name,
                productType,
                productName: template?.productItem?.name ?? template.productGroup?.name,
                code: template.productItem?.businessCode ?? template.productGroup?.businessCode
            }));
        }
        else
            // Product Items
            return productItemIds
                .map(itemId => ({
                    key: itemId,
                    title: productItems[itemId].name,
                    productType,
                    description: productItems[itemId].description,
                    code: productItems[itemId].businessCode
                }));
    });

    const [value, setValue] = useState(() => {
        const id =  draftQuoteDetail.templateId ?? draftQuoteDetail.productItemId ?? draftQuoteDetail.productGroupId;
        if (id) {
            return productOptions.find(option => option.key === id) || null;
        }
        return null;
    });    
    
    return (
        <AccordionSummary 
            id={`add-product-header-${quoteDetailId}`}
            component="div"
            aria-controls="product-groups"
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
                backgroundColor: 'grey.200',
                borderLeft: 5,
                borderLeftColor:  allQuoteDetailsSaved[quoteDetailId] ? theme.palette.success.main : theme.palette.warning.main,
                borderBottom: 1, 
                borderBottomColor: 'grey.500',
                py: 1,
                px: 2
            }}
        >
            <Grid container>
                <Grid 
                    size={{xs: 10, md: 7}}
                    display="flex"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Autocomplete
                        id={`product-${quoteDetailId}`}
                        options={!loading ? productOptions : []}
                        disabled={isSubmitting || draftQuoteDetail.id !== undefined}
                        groupBy={(option) => option.productType}
                        getOptionLabel={(option) => option.title || ''}
                        value={value || null}
                        loading={loading}
                        onChange={handleProductSelection}
                        onClick={(e) => e.stopPropagation()}
                        noOptionsText={productsStatus === 'failed' && quoteTemplatesStatus === 'failed' ? "Failed to load products. Please try again later." : 'No options found.'}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField {...params}
                                       error={!!error}
                                       helperText={error ? error : ""}
                                       label={loading ? "Loading..." : "Select a Product"}
                                       required={!loading}
                                       sx={{
                                           mr: 1,
                                           width: {xs: '200px !important', sm: '250px !important', md: '300px !important'},
                                           "& .MuiInputBase-input": {
                                               overflow: "hidden",
                                               textOverflow: "ellipsis"
                                           }
                                       }}
                                       slotProps={{
                                           input: {
                                               ...params.InputProps,
                                               endAdornment: (
                                                   <React.Fragment>
                                                       {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 2}} /> : null}
                                                       {params.InputProps.endAdornment}
                                                   </React.Fragment>
                                               ),
                                           },
                                       }}
                            />
                        )}
                    />
                    <ConfirmationDialog
                        header="Confirm"
                        title="Remove Product?"
                        description="Are you sure you want to remove this product?"
                        confirmButtonText={"Remove"}
                        cancelButtonText={"Cancel"}
                        response={() => handleProductRemoveFromQuote(quoteDetailId)}
                    >
                        {(showDialog) => (
                            <IconButton
                                variant="text"
                                sx={{ ml: 1}}
                                onClick={showDialog}
                            >
                                <DeleteIcon color="error" />
                            </IconButton>
                        )}
                    </ConfirmationDialog>
                </Grid>
                {value && <>
                {
                    value.productName && <Grid size={{xs:12}}>
                        <Typography variant="subtitle2" sx={{p: 1}}>Product: {value?.productName}</Typography>
                    </Grid>
                }
                    <Grid size={{xs:12}}>
                        <Typography variant="subtitle2" sx={{p: 1}}>Code: {value?.code}</Typography>
                    </Grid>
                    <Grid size={{xs:12}}>
                        <Typography variant="body2" sx={{pl: 1}}>{value?.description}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }} display="flex" sx={{ mt: 2}}>
                        <Typography variant="subtitle2" sx={{p: 1}}>Quantity:</Typography>
                        <NumberInput 
                            onClick={(e) => e.stopPropagation()}
                            min={1} 
                            value={quantityValue}
                            size={"small"} 
                            onChange={handleQuantityChange}/>
                    </Grid>
                </>}
            </Grid>
        </AccordionSummary>
    );
}

export default ProjectDetailRowAccordionSummary;